import { useEffect, useRef } from "react";
import anime from "animejs";
import { ProgressRing } from "./ProgressRing";
import { Loader2, CheckCircle, AlertCircle, Upload } from "lucide-react";

interface Counts {
  uploading: number;
  extracting: number;
  complete: number;
  failed: number;
  total: number;
}

interface ProcessingHeaderProps {
  counts: Counts;
  elapsed: string;
  isComplete: boolean;
}

export function ProcessingHeader({ counts, elapsed, isComplete }: ProcessingHeaderProps) {
  const progress = counts.total > 0 ? counts.complete / counts.total : 0;
  const errorProgress = counts.total > 0 ? counts.failed / counts.total : 0;

  return (
    <div className="card sticky top-0 z-10 flex items-center gap-5 bg-panel/95 px-5 py-4 backdrop-blur-sm">
      <ProgressRing
        progress={progress}
        errorProgress={errorProgress}
        completed={counts.complete}
        total={counts.total}
        isComplete={isComplete && counts.failed === 0}
      />

      <div className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-1">
        {counts.uploading > 0 && (
          <CounterPill
            icon={<Upload className="h-3 w-3" strokeWidth={1.6} />}
            count={counts.uploading}
            label="uploading"
            color="text-ink-2"
          />
        )}
        {counts.extracting > 0 && (
          <CounterPill
            icon={<Loader2 className="h-3 w-3 animate-spin" />}
            count={counts.extracting}
            label="extracting"
            color="text-info"
          />
        )}
        <CounterPill
          icon={<CheckCircle className="h-3 w-3" strokeWidth={1.6} />}
          count={counts.complete}
          label="complete"
          color="text-ok"
        />
        {counts.failed > 0 && (
          <CounterPill
            icon={<AlertCircle className="h-3 w-3" strokeWidth={1.6} />}
            count={counts.failed}
            label="failed"
            color="text-bad"
          />
        )}
      </div>

      <span className="shrink-0 font-mono text-xs text-ink-3">
        {elapsed}
      </span>
    </div>
  );
}

function CounterPill({
  icon,
  count,
  label,
  color,
}: {
  icon: React.ReactNode;
  count: number;
  label: string;
  color: string;
}) {
  const numberRef = useRef<HTMLSpanElement>(null);
  const prevCount = useRef(count);

  useEffect(() => {
    const el = numberRef.current;
    if (!el || count === prevCount.current) {
      prevCount.current = count;
      return;
    }

    const from = prevCount.current;
    prevCount.current = count;

    // Animate the number change
    const obj = { val: from };
    anime({
      targets: obj,
      val: count,
      round: 1,
      easing: "easeOutExpo",
      duration: 400,
      update: () => {
        el.textContent = String(obj.val);
      },
    });
  }, [count]);

  return (
    <span className={`flex items-center gap-1.5 text-xs ${color}`}>
      {icon}
      <span ref={numberRef} className="font-mono font-medium">
        {count}
      </span>
      <span className="text-ink-3">{label}</span>
    </span>
  );
}
